/**
 * Number formatting for large incremental values.
 * Pure — no DOM, no locale dependencies beyond Intl for small numbers.
 */

const SUFFIXES = [
  '', 'K', 'M', 'B', 'T',
  'Qa', 'Qi', 'Sx', 'Sp', 'Oc', 'No',
  'Dc', 'UDc', 'DDc', 'TDc', 'QaDc', 'QiDc', 'SxDc', 'SpDc', 'OcDc', 'NoDc',
  'Vg', 'UVg', 'DVg', 'TVg', 'QaVg', 'QiVg', 'SxVg', 'SpVg', 'OcVg', 'NoVg',
  'Tg',
];

export type Notation = 'standard' | 'scientific';

/** Format a quantity for display. Handles 0 → 1e308 gracefully. */
export function formatNumber(
  value: number,
  notation: Notation = 'standard',
  decimals = 2,
): string {
  if (!Number.isFinite(value)) return '∞';
  if (value === 0) return '0';

  const isNegative = value < 0;
  const magnitude = Math.abs(value);
  const formatted = formatMagnitude(magnitude, notation, decimals);
  return isNegative ? `-${formatted}` : formatted;
}

function formatMagnitude(magnitude: number, notation: Notation, decimals: number): string {
  if (magnitude < 0.01) return magnitude.toExponential(1);

  // Below 1000 show a couple of decimals, trimming trailing zeroes.
  if (magnitude < 1000) {
    const places = magnitude < 10 ? decimals : magnitude < 100 ? 1 : 0;
    return trimZeroes(magnitude.toFixed(places));
  }

  const tier = Math.floor(Math.log10(magnitude) / 3);

  if (notation === 'scientific' || tier >= SUFFIXES.length) {
    const exponent = Math.floor(Math.log10(magnitude));
    const mantissa = magnitude / Math.pow(10, exponent);
    return `${trimZeroes(mantissa.toFixed(decimals))}e${exponent}`;
  }

  const scaled = magnitude / Math.pow(1000, tier);
  const places = scaled < 10 ? decimals : scaled < 100 ? 1 : 0;
  return `${trimZeroes(scaled.toFixed(places))}${SUFFIXES[tier]}`;
}

function trimZeroes(text: string): string {
  return text.includes('.') ? text.replace(/\.?0+$/, '') : text;
}

/** Format a per-second rate, e.g. "+12.4/s" or "-3/s". */
export function formatRate(ratePerSecond: number, notation: Notation = 'standard'): string {
  const sign = ratePerSecond > 0 ? '+' : '';
  return `${sign}${formatNumber(ratePerSecond, notation)}/s`;
}

/** Format a whole-number count (population, building counts). */
export function formatCount(value: number, notation: Notation = 'standard'): string {
  if (Math.abs(value) < 1000) return String(Math.floor(value));
  return formatNumber(Math.floor(value), notation, 2);
}

/** Format a percentage from a multiplier fraction: 0.25 → "+25%". */
export function formatPercent(fraction: number, showSign = true): string {
  const percent = fraction * 100;
  const sign = showSign && percent > 0 ? '+' : '';
  const places = Math.abs(percent) < 10 && percent % 1 !== 0 ? 1 : 0;
  return `${sign}${percent.toFixed(places)}%`;
}

/** Human-readable duration from seconds: "2h 14m", "45s", "3d 4h". */
export function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '—';
  if (totalSeconds < 1) return 'moments';

  const seconds = Math.floor(totalSeconds % 60);
  const minutes = Math.floor((totalSeconds / 60) % 60);
  const hours = Math.floor((totalSeconds / 3600) % 24);
  const days = Math.floor(totalSeconds / 86400);

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Time until `target` is reached given a current amount and rate.
 * Returns Infinity when unreachable.
 */
export function timeToTarget(current: number, target: number, ratePerSecond: number): number {
  if (current >= target) return 0;
  if (ratePerSecond <= 0) return Infinity;
  return (target - current) / ratePerSecond;
}

/** Geometric cost curve used by buildings and ancestral nodes. */
export function scaledCost(baseCost: number, growth: number, owned: number): number {
  return baseCost * Math.pow(growth, owned);
}

/** Total cost of buying `quantity` more of something on a geometric curve. */
export function bulkCost(
  baseCost: number,
  growth: number,
  owned: number,
  quantity: number,
): number {
  if (growth === 1) return baseCost * quantity;
  return (
    (baseCost * Math.pow(growth, owned) * (Math.pow(growth, quantity) - 1)) / (growth - 1)
  );
}

/** Largest affordable quantity on a geometric curve given a budget. */
export function maxAffordable(
  baseCost: number,
  growth: number,
  owned: number,
  budget: number,
): number {
  if (budget <= 0) return 0;
  if (growth === 1) return Math.floor(budget / baseCost);
  const inner = (budget * (growth - 1)) / (baseCost * Math.pow(growth, owned)) + 1;
  if (inner <= 0) return 0;
  return Math.max(0, Math.floor(Math.log(inner) / Math.log(growth)));
}

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
